import receiptLookupTemplate from './receiptLookup.template.html';

var mFilters = {};

/*******************************************************************************
 * Component */
/**
 * @ngdoc   component
 * @module  csm.books.returnReceipts
 * @name    csm.books.returnReceipts.component:receiptLookup
 * @description
 * Provides an ability to search for receipts by Receipt From information
 * (Student ID or Customer Name), Receipt Number, or Item Barcode.
 *******************************************************************************/
export const receiptLookupComponent = {
  template: receiptLookupTemplate,
  controller: Controller
};

/*******************************************************************************
 * Controller */
/**
 * Controls the data flow for the controller.
 *******************************************************************************/
/* @ngInject */ function Controller($injector, BooksSettings, BusyScreen) {
  /** Self reference. */ var $ctrl = this;
  $ctrl.filters = mFilters;

  /*----------------------------------------------------------------*/
  /* Label/search-target helpers for the Customer typeahead - same  */
  /* BooksSettings functions POS's queryAddressEntities typeahead    */
  /* uses.                                                            */
  /*----------------------------------------------------------------*/
  $ctrl.entityLabel = BooksSettings.addressEntityLabel;
  $ctrl.addressEntitySearchTargets = BooksSettings.addressEntitySearchTargets;

  /*------------------*/
  /* Injector Methods */
  /*------------------*/
  $ctrl.buildGrid = $injector.invoke(buildGrid, null, { $ctrl: $ctrl });
  $ctrl.findEntities = $injector.invoke(findEntities, null, { $ctrl: $ctrl });
  $ctrl.returnItems = $injector.invoke(returnItems, null, { $ctrl: $ctrl });
  $ctrl.search = $injector.invoke(search, null, { $ctrl: $ctrl });

  /*-------------------*/
  /* Application Start */
  /*-------------------*/
  $ctrl.buildGrid();

  // Clear default busy screen from bookscontroller inheritance.
  BusyScreen.setBusy(false);

  /*------------------------------------------------------------*/
  /* mFilters persists across re-navigating to this component   */
  /* within the same session (see its declaration at the top of */
  /* this file) - if a prior visit left filters set, re-run the */
  /* same search now instead of waiting for the user to hit     */
  /* Search again.                                               */
  /*------------------------------------------------------------*/
  for (var prop in mFilters)
    if (mFilters.hasOwnProperty(prop)) {
      $ctrl.search();
      break;
    }

  /*****************************************************************************
   * buildGrid */
  /**
   * @ngdoc    method
   * @name     buildGrid
   * @methodof returnReceipts.controller:Controller
   * @description
   * Builds the data grid to hold the list of receipts.
   *****************************************************************************/
  /* @ngInject */ function buildGrid($ctrl, uiGridConstants) {
    /*---------------------------------*/
    /* Template to add double click    */
    /* functionality to each grid row. */
    /*---------------------------------*/
    var dblClickTemplate =
      '\
    <div ui-grid-cell\
          ng-dblclick="grid.appScope.$ctrl.returnItems(row.entity)"\
            ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name"\
                class="ui-grid-cell">\
    </div>';

    /*------------------------------------------------------*/
    /* Template for action buttons in a column of the grid. */
    /*------------------------------------------------------*/
    var actionsCellTemplate =
      '\
    <div class="ui-grid-cell-contents ng-binding ng-scope">\
      <span style="cursor:pointer"\
            class="glyphicon glyphicon-search csm-grid-space-target"\
            title="Return"\
          ng-click="grid.appScope.$ctrl.returnItems(row.entity)">\
      </span>\
    </div>';

    return function () {
      $ctrl.receiptsGrid = {
        enableCellEdit: false,
        enableCellEditOnFocus: true,
        enableColumnMenus: false,
        enableFiltering: false,
        enableRowSelection: false,
        enableFullRowSelection: false,
        enableRowHeaderSelection: false,
        multiSelect: false,
        enableSelectAll: false,
        saveFilter: false,
        saveSort: false,
        rowTemplate: dblClickTemplate,
        enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
        enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
        onRegisterApi: function (gridApi) {
          $ctrl.receiptsGrid.api = gridApi;
        },
        data: [],
        columnDefs: [
          {
            field: 'entryDate',
            cellFilter: 'date:"MM/dd/yyyy"',
            displayName: 'Date',
            width: 90
          },
          {
            field: 'number',
            displayName: 'Receipt #',
            width: 90
          },
          {
            field: 'receivedFrom',
            width: 350
          },
          {
            field: 'memo'
          },
          {
            field: 'amount',
            cellFilter: 'currency',
            type: 'number',
            width: 90
          },
          {
            field: 'status',
            width: 150,
            cellTooltip: true,
            labelFunction: function (receipt) {
              const mainStatus = receipt.cleared
                ? 'Cleared'
                : receipt.deposited
                  ? 'Deposited'
                  : '';

              const returnStatus = receipt.returned
                ? 'Returned'
                : receipt.partiallyReturned
                  ? 'Partially Returned'
                  : '';

              return mainStatus && returnStatus
                ? `${mainStatus} & ${returnStatus}`
                : mainStatus || returnStatus || '';
            }
          },
          {
            width: 40,
            cellTemplate: actionsCellTemplate,
            enableFilter: false,
            enableSorting: false,
            field: 'actions',
            displayName: '',
            pinnedRight: true,
            appScope: $ctrl,
            type: ''
          }
        ]
      };
    };
  }

  /*****************************************************************************
   * findEntities */
  /**
   * @ngdoc    method
   * @name     findEntities
   * @methodof csm.books.returnReceipts.controller:Controller
   * @description
   * Dataprovider for the Customer lazy-typeahead. Used to preload the whole
   * org's entity roster into memory for client-side filtering - expensive
   * to fetch and wasteful for resolving a single entityID. This instead
   * mirrors Point of Sale's queryAddressEntities: a server-side, prefix-
   * indexed search scoped to what the user actually typed, called at most
   * once per leading character (see lazyTypeahead.js's caching).
   *
   * @param   value  Input text to search for.
   * @return         Promise that resolves with the matching entities.
   *****************************************************************************/
  /* @ngInject */ function findEntities($ctrl, $q, BooksSettings, BooksSvc) {
    return function (value) {
      if (!value) return $q.resolve([]);

      return BooksSvc.findAddressEntities(
        value,
        BooksSettings.ledgerEntityTypes()
      ).then(function (resp) {
        return resp.data;
      });
    };
  }

  /*****************************************************************************
   * returnItems */
  /**
   * @ngdoc    method
   * @name     returnItems
   * @methodof csm.books.returnReceipts.controller:Controller
   * @description
   * Send the program to the next route with the selected receipt information.
   *****************************************************************************/
  /* @ngInject */ function returnItems($ctrl, $location, Alert) {
    return function (receipt) {
      if (!receipt) {
        Alert.error('No receipt selected');
        return;
      }

      $location.path('/receiptItems/' + receipt.id);
    };
  }

  /*****************************************************************************
   * search */
  /**
   * @ngdoc    method
   * @name     search
   * @methodof csm.books.returnReceipts.controller:Controller
   * @description
   * Uses the filters povided, and searches for any receipts that match.
   * Results come back one page at a time; the grid-pagination component
   * below the results grid calls this with the page the user asked for.
   *
   * @param {number} page Page number to fetch, defaults to the first page.
   *****************************************************************************/
  /* @ngInject */ function search(
    $ctrl,
    $q,
    BooksServices,
    BooksSettings,
    BusyScreen,
    ReturnsService,
    Tools
  ) {
    return function (page) {
      /*----------------------------------------------------------------*/
      /* If "CR-" is added, we want to extract only the receipt number. */
      /* A receipt-number search with no digits at all (ex. "abc",      */
      /* "CR-") can never match a receipt, and sending the raw string   */
      /* would fail to parse into the server's numeric filter (500). So */
      /* short-circuit to an empty result instead of calling the server.*/
      /*----------------------------------------------------------------*/
      if ($ctrl.filters.receiptNumber) {
        var m = $ctrl.filters.receiptNumber.match(/(\d+)/i);
        if (!m) {
          $ctrl.receiptsGrid.data = [];
          $ctrl.pagination = null;
          return;
        }
        $ctrl.filters.receiptNumber = m[0];
      }

      /*----------------------------------------------------------------*/
      /* $ctrl.filters.customer is either a real AddressEntityListItem  */
      /* (a match was picked - see findEntities) or the raw typed text  */
      /* otherwise. Only the former carries an entityID; the latter     */
      /* falls back to a plain receiptFrom text filter (LIKE match      */
      /* server-side), same as before.                                  */
      /*----------------------------------------------------------------*/
      var customer = $ctrl.filters.customer;
      var selectedCustomer =
        customer && customer.entity ? customer.entity : null;

      /*------------------------------------------------*/
      /* Build the filter object to send to the server. */
      /*------------------------------------------------*/
      var filters = {
        receiptNumber: $ctrl.filters.receiptNumber || '',
        receiptFrom: selectedCustomer ? selectedCustomer.name : customer || '',
        itemBarcode: $ctrl.filters.itemBarcode,
        /**
         * Number of receipts fetched per page. Keeps the search fast since the
         * receipt status has to be computed for every returned receipt.
         * */
        page: page || 1,
        pageSize: 50
      };

      if (selectedCustomer) filters.entityID = selectedCustomer.id;

      /*----------------------------------------------------------------*/
      /* FindReceiptsFilters (server) has no studentID field - it only   */
      /* worked before because the whole org's entity list was preloaded */
      /* and scanned client-side. Without that list, resolve it via a    */
      /* single targeted lookup through BooksServices.Entities instead - */
      /* its promise resolves with the unwrapped array directly          */
      /* (HTTPService.make()), not {data: [...]}.                        */
      /*----------------------------------------------------------------*/
      var resolveEntityID =
        filters.entityID || Tools.isEmpty($ctrl.filters.studentID)
          ? $q.resolve()
          : BooksServices.Entities.findAddressEntitiesAndStudents(
              $ctrl.filters.studentID,
              BooksSettings.ledgerEntityTypes()
            ).then(function (entities) {
              var match = (entities || []).find(function (aeli) {
                return aeli.entity.studentID === $ctrl.filters.studentID;
              });

              if (match) filters.entityID = match.entity.id;
            });

      /*----------------------------*/
      /* Send filter to the server. */
      /*----------------------------*/
      BusyScreen.promise(
        resolveEntityID
          .then(function () {
            return ReturnsService.search(filters);
          })
          .then(function (resp) {
            $ctrl.receiptsGrid.data = resp.receipts;

            // The grid-pagination component reads the paging info
            // (page/totalPages/totalReceiptCount) off of the response.
            $ctrl.pagination = resp;
          })
      );
    };
  }
}
